import { useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  USER_LIST_FETCH_BEGIN,
  USER_LIST_FETCH_SUCCESS,
  USER_LIST_FETCH_FAILURE,
  USER_LIST_UPDATE_SUCCESS,
  USER_DISCORD_UPDATE_SUCCESS,
  USER_SOURCE_FETCH_BEGIN,
  USER_SOURCE_FETCH_SUCCESS,
  USER_SOURCE_FETCH_FAILURE,
  USER_SOURCE_DATASET_FETCH_BEGIN,
  USER_SOURCE_DATASET_FETCH_SUCCESS,
  USER_SOURCE_DATASET_FETCH_FAILURE,
  HEADERS_SET_SUCCESS,
  USER_INFO_UPDATE_SUCCESS,
  UPDATE_USER_LIST_FILTER,
  BATCH_ASSIGN_USERS_TAGS,
  BATCH_UNASSIGN_USERS_TAGS,
} from "./constants";
import _ from "lodash";
import axios from "axios";
import { apiUrl } from "features/configure";
import { enqueueSnackbar } from "features/common/redux/actions";

export const USER_FILTER_INITIAL_STATE = {
  userListFilter: {
    page: 1,
    orderData: { sort: "walletTotalValue", order: -1 },
    search: "",
    cexLabels: [],
    tags: [],
    ecosystems: [],
    chainIds: [],
    assets: [],
  },
};

export function fetchUserAdminList({
  entityName,
  filter = USER_FILTER_INITIAL_STATE.userListFilter,
  signal = null,
}) {
  return async (dispatch, getState) => {
    dispatch({
      type: USER_LIST_FETCH_BEGIN,
    });

    try {
      const result = await axios.post(
        `${apiUrl}/api/user/${entityName}/userList`,
        {
          ...filter,
        },
        {
          signal,
        }
      );
      dispatch({
        type: USER_LIST_FETCH_SUCCESS,
        data: result.data.users,
        importStatus: result.data.importStatus,
        importCount: result.data.importCount,
        userListCount: result.headers["x-total-count"],
      });
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("Request canceled", err.message);
      } else {
        console.log(err);
        dispatch({
          type: USER_LIST_FETCH_FAILURE,
        });
        dispatch(
          enqueueSnackbar({
            message: _.get(
              err,
              "response.data.error",
              _.get(err, "response.data.message", "error")
            ),
            options: {
              variant: "error",
            },
          })
        );
      }
    }
  };
}

export function updateUserListFilter(data) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_USER_LIST_FILTER,
      data,
    });
  };
}

export function fetchUserSource({ entityName }) {
  return async (dispatch) => {
    dispatch({
      type: USER_SOURCE_FETCH_BEGIN,
    });

    try {
      let userSource = await axios.get(
        `${apiUrl}/api/userSource/${entityName}`
      );

      dispatch({
        type: USER_SOURCE_FETCH_SUCCESS,
        data: userSource.data,
      });
    } catch (err) {
      dispatch({
        type: USER_SOURCE_FETCH_FAILURE,
      });
      dispatch(
        enqueueSnackbar({
          message: _.get(
            err,
            "response.data.error",
            _.get(err, "response.data.message", "error")
          ),
          options: {
            variant: "error",
          },
        })
      );
    }
  };
}

export function fetchUserSourceDataset({ entityName }) {
  return async (dispatch) => {
    dispatch({
      type: USER_SOURCE_DATASET_FETCH_BEGIN,
    });

    try {
      let dataset = await axios.get(
        `${apiUrl}/api/userSource/${entityName}?type=dataset`
      );

      dispatch({
        type: USER_SOURCE_DATASET_FETCH_SUCCESS,
        data: dataset.data,
      });
    } catch (err) {
      dispatch({
        type: USER_SOURCE_DATASET_FETCH_FAILURE,
      });
      dispatch(
        enqueueSnackbar({
          message: _.get(
            err,
            "response.data.error",
            _.get(err, "response.data.message", "error")
          ),
          options: {
            variant: "error",
          },
        })
      );
    }
  };
}

export function updateUser({ entityName, uid, userTagIds }) {
  return (dispatch, getState) => {
    const promise = new Promise(async (resolve, reject) => {
      try {
        let users = await axios.post(
          `${apiUrl}/api/manage/tags/${entityName}/user/${uid}/apply`,
          {
            tagIds: userTagIds,
          }
        );

        dispatch({
          type: USER_LIST_UPDATE_SUCCESS,
          data: users.data,
        });
        resolve(users.data);
      } catch (err) {
        console.log(err);
        dispatch(
          enqueueSnackbar({
            message: _.get(err, "response.data.error", "err"),
            options: {
              variant: "error",
            },
          })
        );
        reject();
      }
    });

    return promise;
  };
}

export function batchApplyUserTags({ entityName, uids, tagIds, tagData }) {
  return (dispatch, getState) => {
    const promise = new Promise(async (resolve, reject) => {
      try {
        const data = { type: "user", tagIds, targetIds: uids };
        await axios.post(
          `${apiUrl}/api/manage/tags/${entityName}/batchApply/user`,
          data
        );
        dispatch({
          type: BATCH_ASSIGN_USERS_TAGS,
          data: { uids, tagData },
        });
        resolve();
      } catch (err) {
        console.log(err);
        dispatch(
          enqueueSnackbar({
            message: _.get(err, "response.data.error", "err"),
            options: {
              variant: "error",
            },
          })
        );
        reject();
      }
    });

    return promise;
  };
}

export function batchUnApplyUserTags({ entityName, uids, tagIds, tagData }) {
  return (dispatch, getState) => {
    const promise = new Promise(async (resolve, reject) => {
      try {
        const data = { type: "user", tagIds, targetIds: uids };

        await axios.post(
          `${apiUrl}/api/manage/tags/${entityName}/batchRemove/user`,
          data
        );
        dispatch({
          type: BATCH_UNASSIGN_USERS_TAGS,
          data: { unassignUsers: uids, unassignTags: tagData },
        });
        resolve();
      } catch (err) {
        console.log(err);
        dispatch(
          enqueueSnackbar({
            message: _.get(err, "response.data.error", "err"),
            options: {
              variant: "error",
            },
          })
        );
        reject();
      }
    });

    return promise;
  };
}

export function setSavedHeaderItems({ selectedTab, headers }) {
  return (dispatch) => {
    dispatch({
      type: HEADERS_SET_SUCCESS,
      selectedTab,
      headers,
    });
  };
}

export function useFetchUserList() {
  const dispatch = useDispatch();

  const {
    userInfos,
    userList,
    userSource,
    userSourceDataset,
    userSourceCount,
    userAdminList,
    fetchUserListPending,
    fetchUserListDone,
    fetchUserInfoPending,
    userListCount,
    importStatus,
    importCount,
    fetchUserSourcePending,
    csvHeaders,
    savedHeaderItems,
    userListFilter,
    userHeaderItems,
  } = useSelector(
    (state) => ({
      userList: state.metadesk.userList,
      userInfos: state.metadesk.userInfos,
      userSource: state.metadesk.userSource,
      userSourceDataset: state.metadesk.userSourceDataset,
      userSourceCount: state.metadesk.userSourceCount,
      userAdminList: state.metadesk.userAdminList,
      fetchUserListPending: state.metadesk.fetchUserListPending,
      fetchUserListDone: state.metadesk.fetchUserListDone,
      fetchUserInfoPending: state.metadesk.fetchUserInfoPending,
      userListCount: state.metadesk.userListCount,
      importStatus: state.metadesk.importStatus,
      importCount: state.metadesk.importCount,
      fetchUserSourcePending: state.metadesk.fetchUserSourcePending,
      csvHeaders: state.metadesk.csvHeaders,
      savedHeaderItems: state.metadesk.savedHeaderItems,
      userListFilter: state.metadesk.userListFilter,
      userHeaderItems: state.metadesk.userHeaderItems,
    }),
    shallowEqual
  );

  const fetchUserAdminListAction = useCallback(
    (data) => {
      return dispatch(fetchUserAdminList(data));
    },
    [dispatch]
  );

  const fetchUserSourceAction = useCallback(
    (data) => {
      return dispatch(fetchUserSource(data));
    },
    [dispatch]
  );

  const fetchUserSourceDatasetAction = useCallback(
    (data) => {
      return dispatch(fetchUserSourceDataset(data));
    },
    [dispatch]
  );

  const updateAction = useCallback(
    (data) => {
      return dispatch(updateUser(data));
    },
    [dispatch]
  );

  const setHeaderItemsAction = useCallback(
    (data) => {
      return dispatch(setSavedHeaderItems(data));
    },
    [dispatch]
  );
  const updateUserListFilterAction = useCallback(
    (data) => {
      return dispatch(updateUserListFilter(data));
    },
    [dispatch]
  );

  const batchApplyUserTagsAction = useCallback(
    (data) => {
      return dispatch(batchApplyUserTags(data));
    },
    [dispatch]
  );

  const batchUnApplyUserTagsAction = useCallback(
    (data) => {
      return dispatch(batchUnApplyUserTags(data));
    },
    [dispatch]
  );

  return {
    userList,
    userInfos,
    userSource,
    userSourceDataset,
    userSourceCount,
    userAdminList,
    setSavedHeaderItems: setHeaderItemsAction,
    fetchUserSource: fetchUserSourceAction,
    fetchUserSourceDataset: fetchUserSourceDatasetAction,
    fetchUserAdminList: fetchUserAdminListAction,
    updateUser: updateAction,
    updateUserListFilter: updateUserListFilterAction,
    batchApplyUserTags: batchApplyUserTagsAction,
    batchUnApplyUserTags: batchUnApplyUserTagsAction,
    fetchUserListDone,
    fetchUserListPending,
    userListCount,
    importStatus,
    importCount,
    fetchUserSourcePending,
    csvHeaders,
    savedHeaderItems,
    userListFilter,
    userHeaderItems,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case USER_LIST_FETCH_BEGIN:
      return {
        ...state,
        fetchUserListPending: true,
      };

    case USER_LIST_FETCH_SUCCESS:
      return {
        ...state,
        userList: action.data,
        userListCount: parseInt(action.userListCount),
        importStatus: action.importStatus,
        importCount: parseInt(action.importCount),
        csvHeaders: action.csvHeaders,
        userHeaderItems: action.userHeaderItems,
        fetchUserListDone: true,
        fetchUserListPending: false,
      };
    case BATCH_ASSIGN_USERS_TAGS:
      const { tagData, uids } = action.data;
      const newTagsUserList = _.map(state.userList, (user) => {
        if (_.includes(uids, user._id)) {
          return {
            ...user,
            tags: [
              ...(user?.tags || []),
              ...tagData.filter(
                (newTag) =>
                  !(user?.tags || []).some(
                    (existingTag) => existingTag._id === newTag._id
                  )
              ),
            ],
          };
        }
        return user;
      });
      return {
        ...state,
        userList: newTagsUserList,
      };
    case BATCH_UNASSIGN_USERS_TAGS:
      const { unassignTags, unassignUsers } = action.data;
      const removedTagsUserList = _.map(state.userList, (user) => {
        if (_.includes(unassignUsers, user._id)) {
          return {
            ...user,
            tags: (user?.tags || []).filter(
              (existingTag) =>
                !unassignTags.some(
                  (tagToRemove) => tagToRemove._id === existingTag._id
                )
            ),
          };
        }
        return user;
      });
      return {
        ...state,
        userList: removedTagsUserList,
      };

    case USER_DISCORD_UPDATE_SUCCESS:
      const discordIndex = _.findIndex(state.userList, {
        _id: action.data._id,
      });
      const newUserList = state.userList;
      newUserList[discordIndex] = {
        ...newUserList[discordIndex],
        discordUserName: action.data.discordUserName,
      };
      return {
        ...state,
        userList: newUserList,
      };
    case USER_LIST_UPDATE_SUCCESS:
      const newList = [...state.userList];
      const updatedIndex = _.findIndex(state.userList, {
        _id: action.data._id,
      });
      newList[updatedIndex] = { ...newList[updatedIndex], ...action.data };

      return {
        ...state,
        userList: newList,
      };

    case USER_LIST_FETCH_FAILURE:
      return {
        ...state,
        fetchUserListPending: false,
      };
    case USER_SOURCE_FETCH_BEGIN:
      return {
        ...state,
        fetchUserSourcePending: true,
      };

    case USER_SOURCE_FETCH_SUCCESS:
      return {
        ...state,
        userSource: action.data,
        userSourceCount: action.data.totalCount,
        fetchUserSourcePending: false,
      };
    case USER_SOURCE_FETCH_FAILURE:
      return {
        ...state,
        fetchUserSourcePending: false,
      };
    case USER_SOURCE_DATASET_FETCH_BEGIN:
      return {
        ...state,
        fetchUserSourceDatasetPending: true,
      };

    case USER_SOURCE_DATASET_FETCH_SUCCESS:
      return {
        ...state,
        userSourceDataset: action.data,
        fetchUserSourceDatasetPending: false,
      };
    case USER_SOURCE_DATASET_FETCH_FAILURE:
      return {
        ...state,
        fetchUserSourceDatasetPending: false,
      };
    case HEADERS_SET_SUCCESS:
      return {
        ...state,
        savedHeaderItems: {
          ...state.savedHeaderItems,
          [action.selectedTab]: action.headers,
        },
      };
    case UPDATE_USER_LIST_FILTER:
      return {
        ...state,
        userListFilter: {
          ...USER_FILTER_INITIAL_STATE.userListFilter, // 至少要有初始值
          ...action.data,
        },
      };

    default:
      return state;
  }
}
